import { Observable, Observer } from "rxjs";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  OktaAuth,
  OktaAuthOptions,
  TokenManager,
  AccessToken,
  IDToken,
  isAbsoluteUrl,
} from "@okta/okta-auth-js";

@Injectable({
  providedIn: "root",
})
export class OktaAuthService {
  private loggedInUser: any;
  private _idToken: IDToken;
  private _accessToken: AccessToken;
  private observer: Observer<boolean>;
  $isAuthenticated: Observable<boolean>;
  authConfig: any;
  oktaConfig: OktaAuthOptions;
  oktaAuth: OktaAuth;
  tokenManager: TokenManager;

  constructor(private router: Router) {
    this.$isAuthenticated = new Observable((observer: Observer<boolean>) => {
      this.observer = observer;
      this.isAuthenticated().then((val) => {
        observer.next(val);
      });
    });
  }

  private getHostLocation() {
    return window.location.protocol + "//" + window.location.host;
  }

  public init(oktaConfig: any) {
    return new Promise((resolve, reject) => {
      this.isOktaAuthenticated(oktaConfig)
        .then((res) => {
          return resolve(true);
        })
        .catch((err) => {
          console.log("Error in one of the chains", err);
        });
    });
  }

  isOktaAuthenticated(oktaConfig: any) {
    return new Promise((resolve, reject) => {
      this.initOktaAuth(oktaConfig);
      const path = window.location.pathname;
      const fullPath = this.getHostLocation() + path;
      if (fullPath !== this.oktaConfig.redirectUri) {
        this.isAuthenticated()
          .then(async (res) => {
            await this._getLoggedInUser();
            if (res) {
              return resolve(true);
            } else {
              const queryParam = window.location.search;
              let urlAfterLogin = path;
              if (queryParam) {
                urlAfterLogin += queryParam;
              }
              this.login(urlAfterLogin);
              return reject(false);
            }
          })
          .catch((err) => {
            return reject(false);
          });
      } else {
        this.handleAuthentication().then((res) => {
          return resolve(true);
        }).catch((err) => {
          if (err.errorCode === 'access_denied') {
            this.router.navigate(['401'], { queryParams: { error: err.errorSummary } });
            return resolve(true);
          }
        });
      }
    });
  }

  // invoked after config load
  initOktaAuth(oktaOptions: any) {
    if (this.isTokenExpired()) {
      localStorage.removeItem('okta-token-storage');
      localStorage.removeItem('okta-cache-storage');
      localStorage.removeItem('okta-pkce-storage');
    }
    this.authConfig = JSON.parse(JSON.stringify(oktaOptions));
    if (!isAbsoluteUrl(oktaOptions.okta.redirectUri)) {
      oktaOptions.okta.redirectUri = `${this.getHostLocation()}${oktaOptions.okta.redirectUri}`;
    }
    if (!isAbsoluteUrl(oktaOptions.okta.postLogoutRedirectUri)) {
      oktaOptions.okta.postLogoutRedirectUri = `${this.getHostLocation()}${oktaOptions.okta.postLogoutRedirectUri}`;
    }
    this.oktaConfig = oktaOptions.okta;
    this.oktaAuth = new OktaAuth(this.oktaConfig);
    this.tokenManager = this.oktaAuth.tokenManager;
    this.tokenManager.on("expired", function (key, expiredToken) {
      console.log(`Token with key ${key} has expired!`);
    });
    this.tokenManager.on("renewed", (key, newToken, oldToken) => {
      console.log(`Token with key ${key} has been renewed`);
    });
    this.tokenManager.on("error", (err) => {
      console.log(`Okta token error occurred: ${err}`);
      // Okta had error- will clear one token one its own, but we want to clear it all
      // since we are redirecting to get new ones anyway
      this.clearOktaStorage();
      // redirect to login to get new tokens
      this.login(this.router.url);
    });
  }

  isTokenExpired(): boolean {
    const tokenStorageStr = localStorage.getItem('okta-token-storage');
    if (!tokenStorageStr) {
      return true;
    }
    const tokenStorage = JSON.parse(tokenStorageStr);
    if (!tokenStorage) {
      return true;
    }
    const accessToken = tokenStorage.accessToken;
    if (!accessToken) {
      return true;
    }
    const expiresAt = accessToken.expiresAt;
    if (!expiresAt) {
      return true;
    }
    const currentTimeSeconds = Math.round(Date.now() / 1000);
    if (currentTimeSeconds > expiresAt) {
      // If currentTime is larger, then it is past the expired time and is expired.
      return true;
    }
    return false;
  }

  private clearOktaStorage() {
    for (const key of ["okta-cache-storage", "okta-token-storage"]) {
      localStorage.removeItem(key);
    }
  }

  public async isAuthenticated() {
    this._accessToken = (await this.tokenManager.get(
      "accessToken"
    )) as AccessToken;
    this._idToken = (await this.tokenManager.get("idToken")) as IDToken;
    return !!(this._accessToken && this._idToken);
  }

  login(originalUrl: string) {
    // Save current URL before redirect
    sessionStorage.setItem("okta-app-url", originalUrl || this.router.url);
    let arrScopes = ["openid", "email", "profile", "gi-apps"];
    if (this.authConfig.scopes && this.authConfig.scopes.length > 0) {
      arrScopes = this.authConfig.scopes;
    }
    // Launches the login redirect.
    // https://developer.okta.com/docs/reference/api/oidc/#access-token-scopes-and-claims
    this.oktaAuth.token.getWithRedirect({
      scopes: arrScopes,
    });
  }

  async handleAuthentication() {
    return new Promise(async (resolve, reject) => {
      try {
        if (this.oktaAuth.token.isLoginRedirect()) {
          const tokenContainer = await this.oktaAuth.token.parseFromUrl()
          this.tokenManager.add("idToken", tokenContainer.tokens.idToken);
          this.tokenManager.add("accessToken", tokenContainer.tokens.accessToken);
        }
        const isAuth = await this.isAuthenticated();
        await this._getLoggedInUser();
        const url = sessionStorage.getItem('okta-app-url');
        window.location.href = window.location.origin + url;
        return resolve(true);
      }
      catch (err) {
        reject(err)
      }
    });
  }

  /**
   * @param type "internal", default, For internal users, Clears the local storage and revoke AccessToken.
   * @param type "external", For external users, On completion, this should automatically redirect to logoutRedirectUri.
   */
  async signOut(type: string = "internal") {
    localStorage.clear();
    if (type === "internal") {
      await this.oktaAuth.revokeAccessToken(this.accessToken);
      this._accessToken = (await this.tokenManager.get(
        "accessToken"
      )) as AccessToken;
    } else {
      await this.oktaAuth.signOut();
    }
  }

  get accessToken() {
    return this._accessToken;
  }

  get clientId() {
    return this.authConfig["okta"] && this.authConfig["okta"].clientId
      ? this.authConfig["okta"].clientId
      : "";
  }

  /**
   * Will return the details of the logged in user.
   */
  private async _getLoggedInUser() {
    this._accessToken = (await this.tokenManager.get("accessToken")) as AccessToken;
    if (!this._accessToken) {
      this.loggedInUser = undefined;
      this._accessToken = undefined;
      return;
    }
    this._idToken = (await this.tokenManager.get("idToken")) as IDToken;
    if (!this._idToken || !this._idToken.claims) {
      this.loggedInUser = undefined;
    }
    const name = this._idToken.claims.name;
    let firstName = '';
    let lastName = '';
    if (name) {
      firstName = name.split(' ').slice(0, -1).join(' ');
      lastName = name.split(' ').slice(-1).join(' ');
    }
    this.loggedInUser = {
      firstName,
      lastName,
      userGloballyUniqueIdentifier: this._idToken?.claims?.email,
      email: this._idToken?.claims?.email,
      name: this._idToken?.claims?.name,
      preferred_username: this._idToken?.claims?.preferred_username,
      userId: this._idToken?.claims?.eid,
      accessToken: this._accessToken?.value,
      accessTokenDetails: this._accessToken,
      idTokenDetails: this._idToken
    };
    localStorage.setItem('activeUser', JSON.stringify(this.loggedInUser));
    return this.loggedInUser;
  }

  public getLoggedInUser() {
    return this.loggedInUser;
  }
}
